import { createSelector } from '@reduxjs/toolkit';
import * as go from 'gojs/release/go-module';
import without from 'lodash.without';
import * as React from 'react';
import { emptyArray } from 'vet-bones/constants';
import { isEqual } from 'vet-bones/utils';

import { ProjectResourceType } from 'src/constants/project';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { addProjectModelsToDiagramModel } from 'src/containers/ProjectDiagram/utils/addProjectModelsToDiagramModel';
import { addProjectResourceToDiagramModel } from 'src/containers/ProjectDiagram/utils/addProjectResourceToDiagramModel';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { addSensitivePropertyGroupsFromDatabase } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/addSensitivePropertyGroupsFromDatabase';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { projectSelector } from 'src/selectors/project';
import {
  ProjectFileUpload,
  ProjectModel,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const projectDataSelector = (projectId: string) =>
  createSelector(
    projectSelector(projectId),
    diagramSnapshotSelector(projectId),
    (project, diagram) => ({
      id: project?.id,
      diagram,
      models: Object.values(project?.modelsById || {}),
      fileUploads: Object.values(project?.fileUploadsById || {}),
      virtualGraphs: Object.values(project?.virtualGraphsById || {}),
    })
  );

const useProjectData = () => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectDataSelector(projectId), isEqual);
};

const projectFileUploadsSelector = (projectId: string) =>
  createSelector(projectSelector(projectId), (project) =>
    Object.keys(project?.fileUploadsById || {})
  );

const useAddedFileUploadIds = () => {
  const { projectId } = useRouterParams();
  const fileUploads = useReduxSelector(
    projectFileUploadsSelector(projectId),
    isEqual
  );

  const prevFileUploads = usePrevious(fileUploads);
  const didFileUploadsChange = useDidValueChange(fileUploads);
  const addedFileUploadsIds: string[] =
    didFileUploadsChange && prevFileUploads
      ? without(fileUploads, ...prevFileUploads)
      : (emptyArray as any);

  return addedFileUploadsIds;
};

const projectVirtualGraphsSelector = (projectId: string) =>
  createSelector(projectSelector(projectId), (project) =>
    Object.keys(project?.virtualGraphsById || {})
  );

const useAddedVirtualGraphIds = () => {
  const { projectId } = useRouterParams();
  const virtualGraphs = useReduxSelector(
    projectVirtualGraphsSelector(projectId),
    isEqual
  );

  const prevVirtualGraphs = usePrevious(virtualGraphs);
  const didVirtualGraphsChange = useDidValueChange(virtualGraphs);
  const addedVirtualGraphsIds: string[] =
    didVirtualGraphsChange && prevVirtualGraphs
      ? without(virtualGraphs, ...prevVirtualGraphs)
      : (emptyArray as any);

  return addedVirtualGraphsIds;
};

const isModelPopulated = (model: ProjectModel) =>
  model.classes && model.properties && model.labelsByIri;

const populatedProjectModelIdsSelector = (projectId: string) =>
  createSelector(projectSelector(projectId), (project) =>
    Object.values(project?.modelsById || {})
      .filter((model) => isModelPopulated(model))
      .map((model) => model.id)
  );

const useAddedProjectModelIds = () => {
  const { projectId } = useRouterParams();
  const projectModelIds = useReduxSelector(
    populatedProjectModelIdsSelector(projectId),
    isEqual
  );

  const prevProjectModelIds = usePrevious(projectModelIds);
  const didProjectModelIdsChange = useDidValueChange(projectModelIds);
  const addedProjectModelIds: string[] =
    didProjectModelIdsChange && prevProjectModelIds
      ? without(projectModelIds, ...prevProjectModelIds)
      : (emptyArray as any);

  return addedProjectModelIds;
};

export const useUpdateDiagramData = (diagram: Diagram | undefined) => {
  const projectData = useProjectData();
  const addedFileUploadIds = useAddedFileUploadIds();
  const didAddedFileUploadIdsChange = useDidValueChange(addedFileUploadIds);
  const addedVirtualGraphIds = useAddedVirtualGraphIds();
  const didAddedVirtualGraphIdsChange = useDidValueChange(addedVirtualGraphIds);
  const addedProjectModelIds = useAddedProjectModelIds();
  const didAddedProjectModelIdsChange = useDidValueChange(addedProjectModelIds);
  const databasesById = useReduxSelector(
    (state) => state.databases.databasesById
  );

  React.useEffect(() => {
    if (diagram && didAddedFileUploadIdsChange && addedFileUploadIds.length) {
      const projectFileUploads = addedFileUploadIds
        .map((fileUploadId) =>
          projectData.fileUploads.find(
            (fileUpload) => fileUpload.id === fileUploadId
          )
        )
        .filter((projectFileUpload): projectFileUpload is ProjectFileUpload =>
          Boolean(projectFileUpload?.fields.length)
        );

      if (projectFileUploads.length) {
        commitDiagramModel(
          diagram,
          (model, d) => {
            projectFileUploads.forEach((projectFileUpload) => {
              addProjectResourceToDiagramModel(
                projectFileUpload,
                ProjectResourceType.FILE_UPLOAD
              )(model, d);
            });
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );

        const fileUploadNodes = projectFileUploads
          .map((projectFileUpload) =>
            diagram.findNodeForKey(getResourceKey(projectFileUpload.id))
          )
          .filter((node): node is go.Node => Boolean(node));

        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) =>
            setDiagramSelection(new go.Set(fileUploadNodes))(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );
      }
    }
  }, [addedFileUploadIds, diagram, didAddedFileUploadIdsChange, projectData]);

  React.useEffect(() => {
    if (
      diagram &&
      didAddedVirtualGraphIdsChange &&
      addedVirtualGraphIds.length
    ) {
      const projectVirtualGraphs = addedVirtualGraphIds
        .map((virtualGraphId) =>
          projectData.virtualGraphs.find(
            (virtualGraph) => virtualGraph.id === virtualGraphId
          )
        )
        .filter(
          (projectVirtualGraph): projectVirtualGraph is ProjectVirtualGraph =>
            Boolean(projectVirtualGraph?.fields.length)
        );

      if (projectVirtualGraphs.length) {
        commitDiagramModel(
          diagram,
          (model, d) => {
            projectVirtualGraphs.forEach((projectVirtualGraph) => {
              addProjectResourceToDiagramModel(
                projectVirtualGraph,
                ProjectResourceType.VIRTUAL_GRAPH
              )(model, d);
            });
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );

        const virtualGraphNodes = projectVirtualGraphs
          .map((projectVirtualGraph) =>
            diagram.findNodeForKey(getResourceKey(projectVirtualGraph.id))
          )
          .filter((node): node is go.Node => Boolean(node));

        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) =>
            setDiagramSelection(new go.Set(virtualGraphNodes))(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );
      }
    }
  }, [
    addedVirtualGraphIds,
    diagram,
    didAddedVirtualGraphIdsChange,
    projectData,
  ]);

  React.useEffect(() => {
    if (
      diagram &&
      didAddedProjectModelIdsChange &&
      addedProjectModelIds.length
    ) {
      const projectModels = addedProjectModelIds
        .map((projectModelId: string) => {
          return projectData.models.find(
            (projectModel) => projectModel.id === projectModelId
          );
        })
        .filter((projectModel): projectModel is ProjectModel =>
          Boolean(projectModel)
        );
      // while models can be multiselected, all models are from the same database
      const { databaseId } = projectModels[0];
      const database = databasesById[databaseId];
      commitDiagramModel(
        diagram,
        (model, d) => {
          addProjectModelsToDiagramModel(projectModels)(model, d);
          // this must be called second, since added sensitive property groups
          // depend on pre-existing and added attributes.
          addSensitivePropertyGroupsFromDatabase(database)(model);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
      diagram.zoomToFit();
      diagram.alignDocument(go.Spot.Center, go.Spot.Center);
    }
  }, [
    addedProjectModelIds,
    databasesById,
    diagram,
    didAddedProjectModelIdsChange,
    projectData,
  ]);
};
